import { CardGlass, GlassChip, Typography } from "@cupcodev/ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../app/i18n/I18nProvider";
import type { ReadSectionState } from "../read-models/common/read-model";
import { NaveIcon } from "./NavePage";

export const SectionBoundary = ({
  title,
  state,
  emptyKey,
  children,
  icon,
  count,
  viewAll,
  className,
  id,
}: {
  title: string;
  state: ReadSectionState;
  emptyKey: string;
  children?: ReactNode;
  icon?: string;
  count?: number;
  viewAll?: Readonly<{ to: string; label: string }>;
  className?: string;
  id?: string;
}) => {
  const { t } = useI18n();
  const headingId = id ?? `section-${emptyKey.replace(/\W+/gu, "-")}`;

  return (
    <section
      className={className ? `nave-section ${className}` : "nave-section"}
      aria-labelledby={headingId}
      aria-busy={state === "UNAVAILABLE" ? undefined : false}
    >
      <CardGlass
        variant="glass-md"
        elevation={1}
        className="nave-section-card"
      >
        <div className="section-heading-row">
          <div className="section-heading-title">
            {icon !== undefined ? <NaveIcon name={icon} /> : null}
            <Typography as="h2" id={headingId} variant="title-sm">
              {title}
            </Typography>
            {count !== undefined && count > 0 ? <span className="nave-count">{count}</span> : null}
          </div>

          <div className="section-heading-side">
            {state === "READY" || state === "EMPTY" ? null : (
              <GlassChip variant={sectionStateVariant(state)}>
                {t(`section.state.${state}`)}
              </GlassChip>
            )}
            {viewAll !== undefined && state !== "UNAVAILABLE" ? (
              <Link className="nave-panel-link" to={viewAll.to}>
                {viewAll.label}
                <NaveIcon name="lucide:arrow-right" />
              </Link>
            ) : null}
          </div>
        </div>

        {state === "UNAVAILABLE" ? (
          <div className="section-state section-state-warning">
            <NaveIcon name="lucide:cloud-off" />
            {t("state.unavailable")}
          </div>
        ) : null}

        {state === "STALE" ? (
          <div className="section-state">
            <NaveIcon name="lucide:clock-alert" />
            {t("state.stale")}
          </div>
        ) : null}

        {state === "EMPTY" ? (
          <div className="empty-state">
            <NaveIcon name="lucide:circle-check" />
            {t(emptyKey)}
          </div>
        ) : (
          children
        )}
      </CardGlass>
    </section>
  );
};

const sectionStateVariant = (
  state: ReadSectionState,
): "success" | "warning" | "destructive" | "neutral" => {
  if (state === "READY") return "success";
  if (state === "STALE") return "warning";
  if (state === "UNAVAILABLE") return "destructive";

  return "neutral";
};
